import { mqMax, mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, Dialog, useBreakpoints } from '@sunwebgroup/ui-components/uds'
import React, { PropsWithChildren } from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { AlertService } from '@/scopes/common/components/AlertService/AlertService'
import { useAccommodationContext } from '@/scopes/prices/components/AccommodationContext'
import { AlternativeFilters } from '@/scopes/prices/components/AlternativeFilters/AlternativeFilters'

import { Filters } from '@/scopes/prices/components/Filters'
import { GridTabs } from '@/scopes/prices/components/GridTabs'
import { useGridTabsContext } from '@/scopes/prices/components/GridTabsContext'
import { Messages } from '@/scopes/prices/components/Messages'
import { PriceBasket } from '@/scopes/prices/components/PriceBasket'
import { Grid } from '@/scopes/prices/components/PriceTable/Grid'
import { GridMobile } from '@/scopes/prices/components/PriceTable/GridMobile'
import { Legend } from '@/scopes/prices/components/PriceTable/Legend'
import { MobileNavigation } from '@/scopes/prices/components/PriceTable/MobileNavigation'
import { MonthFilter } from '@/scopes/prices/components/PriceTable/MonthFilter'
import { NavigationByWeeks } from '@/scopes/prices/components/PriceTable/NavigationByWeeks'
import { RoomSelector } from '@/scopes/prices/components/RoomSelector/RoomSelector'
import {
  getFiltersSelector,
  getPricesSelector,
  getTransportSelector,
  isPriceTableLoadingStateSelector,
  pricesResponseSelector,
} from '@/scopes/prices/components/selectors'
import { TransportSelector } from '@/scopes/prices/components/TransportSelector'
import { useScrollToSection } from '@/scopes/prices/domain/prices/hooks'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const PriceTableWrapper = styled.div`
  background: ${tok('primitives_color_neutral_50')};
  min-height: 694px;
  padding: ${space('primitives_space_24')} ${space('primitives_space_24')} 0 ${space('primitives_space_24')};
  display: flex;
  flex-direction: column;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    border-radius: ${tokPx('primitives_corner_radius_xl')};
  }

  @media ${mqMax('primitives_breakpoint_s_max')} {
    padding: 0;
  }
`

const FooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 0;
  padding-right: 0;
  margin-top: ${space('primitives_space_32')};
  gap: ${space('primitives_space_32')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-bottom: ${space('primitives_space_24')};
    margin-top: ${space('primitives_space_32')};
    gap: ${space('primitives_space_32')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    margin-top: ${space('primitives_space_64')};
    gap: ${space('primitives_space_64')};
  }
`

const Tabs = styled(GridTabs)`
  border-bottom: unset;
  padding-left: ${space('primitives_space_12')};
  margin-bottom: ${tokPx('primitives_space_24')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding-left: unset;
    margin-right: 0;
  }

  @media ${mqMax('primitives_breakpoint_s_max')} {
    padding-left: ${space('primitives_space_24')};
  }
`
const TableFooterWrapper = styled.div`
  display: flex;
  gap: ${space('primitives_space_12')};
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 0 ${space('primitives_space_16')} ${space('primitives_space_16')} ${space('primitives_space_16')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex-direction: row;
    justify-content: flex-end;
    margin: 0 0 ${space('primitives_space_24')} 0;
  }
`

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')};
`

const ContentUnavailableWrapper = styled(Dialog)`
  margin: auto;
`

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    margin-left: ${space('primitives_space_24')};
    margin-right: ${space('primitives_space_24')};
  }
`

function PriceConfiguratorWrapper(props: PropsWithChildren) {
  const { children } = props

  return (
    <PriceTableWrapper>
      <Filters />
      {children}
    </PriceTableWrapper>
  )
}

function TabsSection() {
  return (
    <Tabs>
      <MonthFilter />
    </Tabs>
  )
}

function MainContent({ isTablet }: { isTablet: boolean }) {
  const intl = useIntl()
  const { error: isError } = usePricesSelector(getPricesSelector)

  if (isError) {
    return (
      <ContentUnavailableWrapper
        text={intl.formatMessage({ id: 'prices_error_title' })}
        supportingText={intl.formatMessage({ id: 'prices_error_message' })}
        type="alert"
        actionsSlot={(
          <Button onClick={() => window.location.reload()}>
            {intl.formatMessage({ id: 'prices_error_reload_button' })}
          </Button>
        )}
      />
    )
  }

  return (
    <MainContentWrapper>
      <MessagesWrapper>
        <Messages />
      </MessagesWrapper>
      <NavigationByWeeks>{isTablet ? <Grid /> : <GridMobile />}</NavigationByWeeks>
      <TableFooterWrapper>
        <Legend />
        <MobileNavigation />
      </TableFooterWrapper>
    </MainContentWrapper>
  )
}

function FooterSection() {
  return (
    <>
      <FooterWrapper>
        <RoomSelector />
        <TransportSelector />
      </FooterWrapper>
      <PriceBasket />
    </>
  )
}

export function PriceConfigurator() {
  const intl = useIntl()
  const { isEmpty, error, alternativeFilters, isLoading, fetchingTransport } = usePricesSelector((state) => {
    const filters = getFiltersSelector(state)
    const prices = pricesResponseSelector(state)
    const transport = getTransportSelector(state)
    return {
      isEmpty: filters.isEmpty,
      error: filters.error,
      alternativeFilters: prices.alternativeFilters,
      isLoading: isPriceTableLoadingStateSelector(state),
      fetchingTransport: transport.loading,
    }
  })

  const { contextItemId, alertServiceDataSourceId } = useAccommodationContext()
  const { selectedTab } = useGridTabsContext()
  const { isTabletOrGreater } = useBreakpoints()

  useScrollToSection(isLoading, 'RoomSelector')
  useScrollToSection(fetchingTransport, 'FlightSelector')
  useScrollToSection(fetchingTransport, 'BusSelector')

  if (error) {
    return <PriceConfiguratorWrapper />
  }

  let mainContent: React.ReactNode

  if (selectedTab === 'alertService') {
    mainContent = (
      <>
        <TabsSection />
        <AlertService contextItemId={contextItemId} alertServiceDataSourceId={alertServiceDataSourceId} />
      </>
    )
  }
  else if (isEmpty) {
    mainContent = (
      <ContentUnavailableWrapper
        text={intl.formatMessage({ id: 'prices_filters_no-results_title' })}
        supportingText={intl.formatMessage({ id: 'prices_filters_no-results_message' })}
        type="alert"
      />
    )
  }
  else if (alternativeFilters) {
    mainContent = (
      <>
        <TabsSection />
        <AlternativeFilters />
      </>
    )
  }
  else {
    mainContent = (
      <>
        <TabsSection />
        <MainContent isTablet={isTabletOrGreater} />
      </>
    )
  }

  return (
    <>
      <PriceConfiguratorWrapper>{mainContent}</PriceConfiguratorWrapper>
      {!(selectedTab === 'alertService' || isEmpty || alternativeFilters) && <FooterSection />}
    </>
  )
}
